import { Box, Button, chakra, Stack, Tooltip } from "@chakra-ui/react";
import { createBlock } from "@wordpress/blocks";
import { useDispatch, useSelect } from "@wordpress/data";
import React from "react";
import { BLOCKS } from "../../../base/constants/blocks";
import { withChakraUIProvider } from "../../hoc";
import { preferencesStore } from "../../store/preferences-store";
import { ScrollArea } from "../scroll-area/ScrollArea";
import InsertersSidebar from "./InsertersSidebar";
import LibrarySidebar from "./LibrarySidebar";

export const ShortcutSidebar = ({
	renderer,
}: {
	renderer: (Component: React.FC, root: HTMLElement) => void;
}) => {
	const [isLibraryPanelOpen, setIsLibraryPanelOpen] = React.useState(false);
	const [isInsertersPanelOpen, setIsInsertersPanelOpen] =
		React.useState(false);

	const inserters = useSelect(
		(select) => select(preferencesStore).getInserters() as Array<string>,
		[]
	);

	const { insertBlock } = useDispatch("core/block-editor");

	const makeRoot = (selector: string) => {
		const mainPanel = document.querySelector(".mzb-shortcut-sidebar");
		if (!mainPanel) return;
		if (document.querySelector(`.${selector}`)) {
			document.querySelector(`.${selector}`)?.remove();
			return false;
		}
		mainPanel.insertAdjacentHTML(
			"afterend",
			`<div class="${selector}"></div>`
		);
		return document.querySelector(`.${selector}`) as HTMLDivElement;
	};

	const toggleLibraryPanel = () => {
		const root = makeRoot("mzb-library-sidebar");
		setIsLibraryPanelOpen(!!root);
		if (!root) {
			return;
		}
		renderer(LibrarySidebar, root);
	};

	const toggleInsertersPanel = () => {
		const root = makeRoot("mzb-inserters-sidebar");
		setIsInsertersPanelOpen(!!root);
		if (!root) {
			return;
		}
		renderer(InsertersSidebar, root);
	};

	return (
		<Box
			w="60px"
			bgColor="white"
			border="1px solid"
			h="full"
			borderColor="gray.200"
		>
			<Stack h="calc(100% - 60px)">
				<Stack alignItems="center">
					<Button
						isActive={isLibraryPanelOpen}
						onClick={toggleLibraryPanel}
						variant="icon"
						sx={{
							svg: {
								w: 6,
								h: 6,
							},
						}}
						my="3"
					>
						<svg
							width="20"
							height="20"
							viewBox="0 0 20 20"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M3.33337 10L10 13.3333L16.6667 10M3.33337 13.3333L10 16.6667L16.6667 13.3333M10 3.33334L3.33337 6.66668L10 10L16.6667 6.66668L10 3.33334Z"
								stroke="#222222"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</Button>
				</Stack>
				<chakra.hr
					mx="auto"
					w="80%"
					bg="gray.400"
					h="1px"
					borderRadius="base"
				/>
				<ScrollArea h="full">
					<Stack alignItems="center">
						{inserters.map((inserter) => {
							const block =
								Object.values(BLOCKS).find(
									(b) => b.blockName === inserter
								) ?? null;
							if (!block) return null;
							return (
								<Tooltip
									key={inserter}
									label={block.label}
									placement="top"
									hasArrow
									closeOnClick={false}
								>
									<Button
										onClick={() =>
											insertBlock(createBlock(inserter))
										}
										variant="icon"
										sx={{
											svg: {
												w: 6,
												h: 6,
												fill: "primary.500",
											},
										}}
										my="3"
									>
										<block.icon />
									</Button>
								</Tooltip>
							);
						})}
					</Stack>
				</ScrollArea>
			</Stack>
			<Box
				h="60px"
				w="60px"
				display="flex"
				alignItems="center"
				justifyContent="center"
			>
				<Button
					isActive={isInsertersPanelOpen}
					variant="icon"
					bgColor="primary.500"
					sx={{
						svg: {
							w: 6,
							h: 6,
						},
					}}
					onClick={toggleInsertersPanel}
					_active={{}}
				>
					<svg
						width="24"
						height="24"
						viewBox="0 0 24 24"
						fill="#690aa0"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M11.25 12.75V18H12.75V12.75H18V11.25H12.75V6H11.25V11.25H6V12.75H11.25Z"
							fill="white"
						/>
					</svg>
				</Button>
			</Box>
		</Box>
	);
};

export default withChakraUIProvider(ShortcutSidebar);
